/**
 * Whose books a device shows (deep-fix r4, table BK; the user's rule, 2026-10-24: books belong to the account, not the
 * device). Each account's books are kept in a book store of their own. The device's first store, from before accounts,
 * belongs to the first account logged in on the device (its claim); until then it is nobody's, shown read-only. With
 * nobody logged in, no account's books show. Pure.
 */
import type { AccountId } from './wire.ts';

/** The store a page shows: the device's first store, an account's own store, or none (an empty one). */
export type Shelf = { readonly kind: 'first' } | { readonly kind: 'own'; readonly account: AccountId } | { readonly kind: 'none' };

/**
 * Part 36c, table FC: the first store's claim once its claimer's account was deleted for good: nobody's again, ever, so
 * no account takes it over (one with a store of its own would lose sight of it) and every account goes to its own.
 */
export const RELEASED = 'released';

/** Who the first store belongs to: an account, nobody yet (null), or nobody ever again ({@link RELEASED}). */
export type Claim = AccountId | typeof RELEASED | null;

/**
 * BK1–BK5, BK9: the shelf for the account logged in (null: nobody) and the first store's claimer (null: unclaimed;
 * undefined: not known, its read or claim failed). BK1 nobody, unclaimed: the first store, read-only (no account permits
 * writing); BK2 nobody, claimed: none; BK3 the claimer: the first store; BK4 an account while unclaimed: the first store,
 * which it claims (claimed before this is asked); BK5 another account: its own. BK9 not known: nobody sees the first
 * store, read-only (its books are never out of reach of export, tenet 3); an account sees none, never another's books
 * to write in. FC1–FC3: a released claim is nobody's: nobody sees none, an account its own.
 */
export function shelfOf(account: AccountId | null, claim: Claim | undefined): Shelf {
  if (account === null) return claim === null || claim === undefined ? { kind: 'first' } : { kind: 'none' };
  if (claim === undefined) return { kind: 'none' };
  return claim === null || claim === account ? { kind: 'first' } : { kind: 'own', account };
}

/** Whether two shelves are the same store. */
export function sameShelf(a: Shelf, b: Shelf): boolean {
  if (a.kind === 'own' || b.kind === 'own') return a.kind === 'own' && b.kind === 'own' && a.account === b.account;
  return a.kind === b.kind;
}
